<template>
  <div class="payment-page">
    <ValidationObserver ref="observer" v-slot="{ handleSubmit, errors, invalid, validated }" slim>
      <form @submit.prevent="handleSubmit(submitPaymentForReview)">
        <h1 id="wb-cont" class="container">{{ $t("paymentinfotitle") }}</h1>
        <div role="alert" v-if="invalid && validated" class="alert alert-danger">
          <h4>
            <span class="container">
              <img src="../assets/circle.png" class="error-icon" alt />
              {{ $t("errorSummary", { errCount: countErrors(errors) }) }}
            </span>
          </h4>
          <ul class="container">
            <li v-for="(error, index) in realErrors(errors)" :key="index" v-html="error[0]"></li>
          </ul>
        </div>
        <div class="container">
          <div class="payment-summary">
            <p>
              {{ $t("totaldue") }}:
              <span class="pull-right">${{ cartInfo.displayTotal }}</span>
            </p>
          </div>

          <div v-for="payment in payments" :key="payment._id">
            <div v-if="payment.internalId" class="applied-payments">
              {{ $t("voucher") }}: {{ payment.internalId }}
              <a href="#" class="pull-right remove-payment" @click.prevent="removePayment(payment._id)">
                <span class="sr-only">{{ $t("removethispayment") }}</span>&times;
              </a>
              <span class="pull-right">${{
                  payment.displayCurrencyFormattedBalanceAvailableForCart
              }}</span>
            </div>
            <div v-if="payment.GCNumber" class="applied-payments">
              {{ $t("gift_certificate") }}: {{ payment.GCNumber }}
              <a href="#" class="pull-right remove-payment" @click.prevent="removePayment(payment.GCNumber)">
                <span class="sr-only">{{ $t("removethispayment") }}</span>&times;
              </a>
              <span class="pull-right">${{ payment.formattedBalance }}</span>
            </div>
          </div>
          <div v-if="cart.promos.length">
            <div v-for="promo in cart.promos" :key="promo.name">
              <div class="applied-payments">
                {{ $t("promocode") }}: "{{ promo.name }}"
                <span class="pull-right"><small>{{ $t("appliedabove") }}</small> ${{
                    promo.total
                }}</span>
              </div>
            </div>
          </div>
          <div class="payment-left" v-if="cartBalance < cartTotal && cartBalance > 0">
            <div class="alert alert-info">
              {{ $t("partialbalance", { cartBalance: cartBalance }) }}
            </div>
          </div>
          <div class="row payment-method" v-if="cartBalance > 0">
            <ValidationProvider :rules="{
              is_not: 'none',
              required: {
                fieldLabel: $t('payment_method'),
                fieldId: '#paymentMethod',
              },
            }" v-slot="{ errors }" slim>
              <div class="col-sm-3">
                <label for="paymentMethod">{{ $t("payment_method") }}</label>
                <select name="paymentMethod" id="paymentMethod" class="form-control" v-model="paymentMethod">
                  <option value="none" selected disabled>
                    {{ $t("select_payment_method") }}
                  </option>
                  <option v-for="paymentMethod in paymentMethods" :key="paymentMethod._id"
                    :value="paymentMethod.method">
                    {{ $t(paymentMethod.method) }}
                  </option>
                </select>
                <span class="label label-danger form-error" v-if="errors.length">
                  <strong>
                    <span class="prefix">{{ $t("errorPrefix") }} </span>
                    <span v-html="errors[0]"></span>
                  </strong>
                </span>
              </div>
            </ValidationProvider>

            <div class="col-sm-4" v-if="payWithCC">
              <ValidationProvider :rules="{
                required: {
                  fieldLabel: $t('card_number'),
                  fieldId: '#cardNumber',
                },
                length: cardType == 'amex' ? 15 : 16,
              }" v-slot="{ errors }" slim>
                <label for="cardNumber" class="card-number-label">
                  {{ $t("card_number") }}
                  <span class="sr-only">{{ $t("accepted_cards") }}</span>
                  <span class="pull-right">
                    <i aria-hidden="true" class="fa fa-cc-visa"></i>
                    <i aria-hidden="true" class="fa fa-cc-amex"></i>
                    <i aria-hidden="true" class="fa fa-cc-mastercard"></i>
                  </span>
                </label>
                <input type="text" name="cardNumber" id="cardNumber" class="form-control" v-model="ccnumber" />
                <span class="label label-danger form-error" v-if="errors.length">
                  <strong>
                    <span class="prefix">{{ $t("errorPrefix") }} </span>
                    <span v-html="errors[0]"></span>
                  </strong>
                </span>
              </ValidationProvider>
            </div>

            <div class="col-sm-3 expiry-group clearfix" v-if="payWithCC">
              <fieldset>
                <legend>{{ $t("expiry") }}</legend>
                <div class="form-group">
                  <select name="cardExpiryMonth" id="cardExpiryMonth" :aria-label="$t('expiry') + ' Month'" class="form-control" v-model="expiryMonth">
                    <option v-for="month in expiryMonths" :key="month" :value="month">
                      {{ month }}
                    </option>
                  </select>
                  <select name="cardExpiryYear" id="cardExpiryYear" class="form-control" :aria-label="$t('expiry') + ' Year'" v-model="expiryYear">
                    <option v-for="year in expiryYears" :key="year" :value="year">
                      {{ year }}
                    </option>
                  </select>
                </div>
              </fieldset>
            </div>
            <div class="col-sm-2" v-if="payWithCC">
              <ValidationProvider :rules="{
                required: { fieldLabel: $t('ccv'), fieldId: '#ccv' },
              }" v-slot="{ errors }" slim>
                <label for="ccv">{{ $t("ccv") }}</label>
                <input type="text" class="form-control" name="ccv" id="ccv" v-model="ccv" />
                <span class="label label-danger form-error" v-if="errors.length">
                  <strong>
                    <span class="prefix">{{ $t("errorPrefix") }} </span>
                    <span v-html="errors[0]"></span>
                  </strong>
                </span>
              </ValidationProvider>
            </div>
            <div class="col-sm-4" v-if="payWithVC">
              <ValidationProvider :rules="{ voucher: { voucherError: voucherError } }" v-slot="{ errors }" slim
                immediate>
                <label for="voucherCode">{{ $t("voucher") }}</label>
                <input type="text" class="form-control" name="voucherCode" id="voucherCode" v-model="voucherCode" />
                <span class="label label-danger form-error" v-if="errors.length">
                  <strong>
                    <span class="prefix">{{ $t("errorPrefix") }} </span>
                    <span v-html="errors[0]"></span>
                  </strong>
                </span>
              </ValidationProvider>
            </div>
            <div class="col-sm-4" v-if="payWithGC">
              <ValidationProvider :rules="{
                giftcertificate: { giftCertificateError: giftCertificateError },
              }" v-slot="{ errors }" slim immediate>
                <label for="giftCertificate">{{
                    $t("gift_certificate")
                }}</label>
                <input type="text" class="form-control" name="giftCertificate" id="giftCertificate"
                  v-model="giftCertificate" />
                <span class="label label-danger form-error" v-if="errors.length">
                  <strong>
                    <span class="prefix">{{ $t("errorPrefix") }} </span>
                    <span v-html="errors[0]"></span>
                  </strong>
                </span>
              </ValidationProvider>
            </div>
          </div>
          <div class="row payment-name">
            <div class="col-sm-6">
              <ValidationProvider :rules="{
                required: {
                  fieldLabel: $t('fname'),
                  fieldId: '#paymentFirstName',
                },
              }" v-slot="{ errors }" slim>
                <div class="form-group">
                  <label for="paymentFirstName">{{ $t("fname") }}</label>
                  <input type="text" name="paymentFirstName" id="paymentFirstName" v-model="firstName"
                    class="form-control" />
                  <span class="label label-danger form-error" v-if="errors.length">
                    <strong>
                      <span class="prefix">{{ $t("errorPrefix") }} </span>
                      <span v-html="errors[0]"></span>
                    </strong>
                  </span>
                </div>
              </ValidationProvider>
            </div>
            <div class="col-sm-6">
              <ValidationProvider :rules="{
                required: {
                  fieldLabel: $t('lname'),
                  fieldId: '#paymentLastName',
                },
              }" v-slot="{ errors }" slim>
                <div class="form-group">
                  <label for="paymentLastName">{{ $t("lname") }}</label>
                  <input type="text" name="paymentLastName" id="paymentLastName" class="form-control"
                    v-model="lastName" />
                  <span class="label label-danger form-error" v-if="errors.length">
                    <strong>
                      <span class="prefix">{{ $t("errorPrefix") }} </span>
                      <span v-html="errors[0]"></span>
                    </strong>
                  </span>
                </div>
              </ValidationProvider>
            </div>
          </div>
          <div class="payment-address" v-if="payWithCC">
            <h2>{{ $t("billing_information") }}</h2>
            <div class="row billing-line1">
              <div class="col-sm-8">
                <ValidationProvider :rules="{
                  required: {
                    fieldLabel: $t('street_address'),
                    fieldId: '#addressStreet',
                  },
                }" v-slot="{ errors }" slim>
                  <label for="addressStreet">{{ $t("street_address") }}</label>
                  <input name="addressStreet" id="addressStreet" class="form-control" v-model="addressStreet" />
                  <span class="label label-danger form-error" v-if="errors.length">
                    <strong>
                      <span class="prefix">{{ $t("errorPrefix") }} </span>
                      <span v-html="errors[0]"></span>
                    </strong>
                  </span>
                </ValidationProvider>
              </div>
              <div class="col-sm-4">
                <ValidationProvider :rules="{
                  required: {
                    fieldLabel: $t('city'),
                    fieldId: '#addressCity',
                  },
                }" v-slot="{ errors }" slim>
                  <label for="addressCity">{{ $t("city") }}</label>
                  <input name="addressCity" id="addressCity" class="form-control" v-model="addressCity" />
                  <span class="label label-danger form-error" v-if="errors.length">
                    <strong>
                      <span class="prefix">{{ $t("errorPrefix") }} </span>
                      <span v-html="errors[0]"></span>
                    </strong>
                  </span>
                </ValidationProvider>
              </div>
            </div>
            <div class="row billing-line2">
              <div class="col-sm-12">
                <div class="row">
                  <div class="col-sm-4">
                    <label for="addressCountry">{{ $t("country") }}</label>
                    <select name="addressCountry" id="addressCountry" class="form-control" v-model="countryId">
                      <option v-for="country in countries" :key="country._id" :value="country._id">
                        {{ country.name }}
                      </option>
                    </select>
                  </div>
                  <div class="col-sm-4">
                    <ValidationProvider :rules="{
                      required: {
                        fieldLabel: $t('province'),
                        fieldId: '#addressProvince',
                      },
                    }" v-slot="{ errors }" slim>
                      <label for="addressProvince">{{ $t("province") }}</label>
                      <template v-if="currentCountryProvinces.length">
                        <select name="addressProvince" id="addressProvince" class="form-control"
                          v-model="addressProvince">
                          <option value disabled>
                            {{ $t("select_province") }}
                          </option>
                          <option v-for="province in currentCountryProvinces" :key="province.name"
                            :value="province.name">
                            {{ province.name }}
                          </option>
                        </select>
                      </template>
                      <template v-else>
                        <input name="addressProvince" id="addressProvince" class="form-control"
                          v-model="addressProvince" />
                      </template>
                      <span class="label label-danger form-error" v-if="errors.length">
                        <strong>
                          <span class="prefix">{{ $t("errorPrefix") }} </span>
                          <span v-html="errors[0]"></span>
                        </strong>
                      </span>
                    </ValidationProvider>
                  </div>
                  <div class="col-sm-4">
                    <ValidationProvider :rules="{
                      required: {
                        fieldLabel: $t('postal_zip'),
                        fieldId: '#addressPostal',
                      },
                    }" v-slot="{ errors }" slim>
                      <label for="addressPostal">{{ $t("postal_zip") }}</label>
                      <input name="addressPostal" id="addressPostal" class="form-control" v-model="addressPostalCode" />
                      <span class="label label-danger form-error" v-if="errors.length">
                        <strong>
                          <span class="prefix">{{ $t("errorPrefix") }} </span>
                          <span v-html="errors[0]"></span>
                        </strong>
                      </span>
                    </ValidationProvider>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="row promo-code">
            <ValidationProvider :rules="{
              promocodeerror: { promoCodeError: promoCodeError },
            }" slim>
              <div class="col-sm-5"><label for="promoCode">{{ $t("promocode") }}</label>
                <div class="form-group form-inline"> <input type="text" name="promoCode" id="promoCode"
                    class="form-control" v-model="promoCode" />
                  <button @click.prevent="applyPromoCode(promoCode)" class="btn btn-primary">{{
                      $t('apply')
                  }}</button>

                </div>
                <span class="label label-danger form-error" v-if="promoCodeError.length">
                  <strong>
                    <span class="prefix">{{ $t("errorPrefix") }} </span>
                    <span v-html="promoCodeError[0]"></span>
                  </strong>
                </span>
              </div>
            </ValidationProvider>
          </div>
        </div>
        <div class="form-group text-center mini-widget--actions">
          <a href="#" class="btn btn-ghost-yellow mrgn-rght-md" @click.prevent="modifyTrip">{{ $t("previous") }}</a>
          
          <button type="submit" id="tripNext" class="btn btn-primary"
            :disabled="cartBalance > 0 && cartBalance !== cartTotal && !payWithCC">
            {{ $t("next_confirm_details") }}
          </button>
          <a href="#" class="btn btn-ghost-gray" @click.prevent="emptyCart"><i class="fa fa-trash"></i> {{ $t("cancel")
          }}</a>
        </div>
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import i18n from "../i18n";
import { ValidationObserver, ValidationProvider, extend } from "vee-validate";
import { required, is_not, length } from "vee-validate/dist/rules";
import { mapState, mapGetters } from "vuex";

// Override the default message.
extend("required", {
  ...required,
  params: ["fieldLabel", "fieldId"],
  message: (fieldName, placeholders) => {
    //return `The <a href="${placeholders.fieldId}">${placeholders.fieldLabel}</a> field is required.`;
    let transMessage = i18n.t("dynamicError", {
      fieldId: placeholders.fieldId,
      fieldLabel: placeholders.fieldLabel,
    });
    return transMessage;
  },
});
extend("is_not", {
  ...is_not,
  message: i18n.t("payment_method_error"),
});
extend("length", {
  ...length,
  message: i18n.t("card_number_length"),
});

extend("voucher", {
  validate(value, args) {
    return value.length && !args.voucherError.length;
  },
  params: ["voucherError"],
  message: i18n.t("voucher_error"),
});

extend("giftcertificate", {
  validate(value, args) {
    return value.length && !args.giftCertificateError.length;
  },
  params: ["giftCertificateError"],
  message: i18n.t("gift_certificate_error"),
});
extend("promocodeerror", {
  validate(value, args) {
    return value.length && !args.promoCodeError.length;
  },
  params: ["promoCodeError"],
  message: '',
});
export default {
  name: "PaymentInformationPage",
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  data() {
    return {
      paymentMethod: "none",
      expiryMonths: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
      expiryMonth: Number,
      expiryYear: Number,
      ccv: "",
      countryId: "",
      currentCountry: [],
      currentCountryProvinces: [],
      firstName: "",
      lastName: "",
      cardType: "",
      ccnumber: "",
      addressStreet: "",
      addressCity: "",
      addressProvince: "",
      addressPostalCode: "",
      giftCertificate: "",
      voucherCode: "",
      promoCode: "",
      voucherError: [],
      giftCertificateError: [],
      promoCodeError: []
    };
  },
  watch: {
    countryId: function (val) {
      this.currentCountry = this.countries.filter(function (country) {
        return country._id === val;
      });
    },
    currentCountry: function (val) {
      this.currentCountryProvinces = val[0].provinces;
    },
    ccnumber: function (number) {
      this.cardType = this.checkCardType(number);
    },
    voucherCode: function (val) {
      // Only check Voucher when we have 11 characters.
      this.$store.dispatch("checkVoucher", val).catch((error) => {
        // eslint-disable-next-line no-console
        console.log("Error getting voucher:", error.response);
        // eslint-disable-next-line no-console
        console.log("voucher error in component.");
        this.voucherError.push(error.response.data.message);
      });
    },
    giftCertificate: function (val) {
      this.$store.dispatch("checkGiftCertificate", val).catch((error) => {
        // eslint-disable-next-line no-console
        console.log("Error getting voucher:", error.response);
        // eslint-disable-next-line no-console
        console.log("voucher error in component.");
        this.giftCertificateError.push(error.response.data.message);
      });
    },
    payments: function (val) {
      if (val.length) {
        this.voucherError = [];
        this.giftCertificateError = [];
      }
    },
  },
  mounted() {
    // this.$store.commit("getCountries");
    // this.$store.commit("getPaymentMethods");
  },
  created() {
    let today = new Date();
    this.expiryMonth = today.getMonth() + 1;
    this.expiryYear = today.getFullYear();
    this.countryId = process.env.VUE_APP_BETTEREZ_DEFAULT_COUNTRY;
    if (this.$store.state.cart.length) {
      this.firstName = this.$store.state.cart.items.tickets[0].passengers[0].firstName;
      this.lastName = this.$store.state.cart.items.tickets[0].passengers[0].lastName;
    }
  },
  computed: {
    cartInfo() {
      return this.$store.state.cart;
    },
    noPaymentsNeeded() {
      return this.cartBalance == 0;
    },
    payWithCC() {
      return (
        this.paymentMethod === "credit" ||
        this.paymentMethod === "online_credit"
      );
    },
    payWithGC() {
      return this.paymentMethod === "gift_certificate";
    },
    payWithVC() {
      return this.paymentMethod === "voucher";
    },
    expiryYears() {
      let today = new Date();
      let thisYear = today.getFullYear();
      let expiryYears = [];
      for (let index = 0; index < 13; index++) {
        expiryYears.push(thisYear + index);
      }
      return expiryYears;
    },
    ...mapState(["countries", "paymentMethods", "payments", "cart"]),
    ...mapGetters(["cartBalance", "cartTotal"]),
  },
  methods: {
    removePayment(paymentId) {
      this.$store.dispatch("removePayment", paymentId);
    },
    checkCardType(number) {
      // visa
      var re = new RegExp("^4");
      if (number.match(re) != null) return "visa";

      // Mastercard
      // Updated for Mastercard 2017 BINs expansion
      if (
        /^(5[1-5][0-9]{14}|2(22[1-9][0-9]{12}|2[3-9][0-9]{13}|[3-6][0-9]{14}|7[0-1][0-9]{13}|720[0-9]{12}))$/.test(
          number
        )
      )
        return "mastercard";

      // AMEX
      re = new RegExp("^3[47]");
      if (number.match(re) != null) return "amex";

      return "";
    },
    modifyTrip: function () {
      this.$router.back();
    },
    applyPromoCode: function (promoCode) {
      //Apply promo code here.
      this.$store.dispatch("applyPromoCode", promoCode).then((response) => {
        this.promoCodeError = [];
        if (response.data.hasOwnProperty('error')) {
          this.promoCodeError.push(i18n.t('promo_not_applied'));
        }
        return response;
      }).catch((error) => {

        this.promoCodeError.push(error.response.data.message);

      });
    },
    realErrors(obj) {
      for (var propName in obj) {
        if (
          obj[propName] === null ||
          obj[propName] === undefined ||
          !obj[propName].length
        ) {
          delete obj[propName];
        }
      }
      return obj;
    },
    countErrors: function (errors) {
      const errorCount = Object.values(errors);
      let count = 0;
      for (let i = 0; i < errorCount.length; i++) {
        if (errorCount[i].length) {
          count++;
        }
      }
      return count;
    },
    submitPaymentForReview: function () {
      this.$router.push("/cart/payment/review");
    },
    emptyCart: function () {
      this.$store.commit("RESET_TRIP");
      this.$store.commit("EMPTY_CART");
      this.$router.push("/");
    },
  },
  filters: {},
  beforeRouteLeave(to, from, next) {
    // called when the route that renders this component is about to
    // be navigated away from.
    // has access to `this` component instance.
    if (this.paymentMethod == "online_credit") {
      this.$store.commit("setPaymentInfo", {
        paymentMethod: this.paymentMethod,
        voucherCode: this.voucherCode,
        giftCertificate: this.giftCertificate,
        ccnumber: this.ccnumber,
        cardType: this.cardType,
        expiryMonth: this.expiryMonth,
        expiryYear: this.expiryYear,
        ccv: this.ccv,
        addressStreet: this.addressStreet,
        addressCity: this.addressCity,
        addressProvince: this.addressProvince,
        countryName: this.currentCountry[0].name,
        country: this.currentCountry[0].ISO,
        addressPostalCode: this.addressPostalCode,
        firstName: this.firstName,
        lastName: this.lastName,
      });
    } else {
      this.$store.commit("setPaymentInfo", {
        paymentMethod: "",
        voucherCode: "",
        giftCertificate: "",
        ccnumber: "",
        cardType: "",
        expiryMonth: "",
        expiryYear: "",
        ccv: "",
        addressStreet: "",
        addressCity: "",
        addressProvince: "",
        countryName: "",
        country: "",
        addressPostalCode: "",
        firstName: this.firstName,
        lastName: this.lastName,
      });
    }
    next();
  },
};
</script>
<style lang="scss">
.clear-left {
  clear: left;
}

.btn-ghost-red {
  margin-left: 15px;
}
.btn-ghost-gray{
  margin-left:20px;
}
#tripNext{
  margin-left:20px;
}

.expiry-group {
  .form-control {
    width: 50%;
    float: left;
  }
}

.applied-payments {
  margin: -30px 20px 30px 20px;
  background-color: #fff;
  border: 1px solid #efefef;
  border-top-width: 0px;
  padding: 10px 25px;
}

.remove-payment {
  padding: 0px 0px 0px 10px;
  font-size: 19px;
  line-height: 17px;
}

.payment-method,
.payment-name,
.billing-line1,
.billing-line2 {
  margin-bottom: 20px;
}

.card-number-label {
  width: 100%;
  position: relative;

  span.pull-right {
    position: absolute;
    right: 0px;
    top: -4px;
  }

  i {
    font-size: 28px;
    padding-left: 8px;
  }
}

.promo-code>div {
  border: 1px solid #efefef;
  padding-top: 15px;
  padding-bottom: 15px;
}

.promo-code input[name="promoCode"] {
  text-transform: uppercase;
}
</style>
